import { useCallback, useId, useMemo, useState } from 'react';
import { makeStyles, tokens, Text, mergeClasses } from '@fluentui/react-components';
import { AddCircleRegular, DocumentBulletListRegular, ArrowDownloadRegular } from '@fluentui/react-icons';
import { isOperationDrag, getOperationFromDrop } from '@/hooks/useToolboxDragDrop';
import type { OperationDefinition } from '@/types/operations';

type EmptyCanvasStateProps = {
  /** Called when user clicks "Start from scratch" */
  onAddTrigger?: () => void;
  /** Called when user clicks "Start from a template" */
  onBrowseTemplates?: () => void;
  /** Called when user drops an operation on the canvas */
  onOperationDrop?: (operation: OperationDefinition) => void;
};

/**
 * ARIA APG button-pattern keyboard handlers for a non-<button> element:
 * Enter activates on keydown; Space prevents page scroll on keydown and
 * activates on keyup (matching native button semantics).
 */
function useButtonKeys(onActivate: (() => void) | undefined) {
  return useMemo(() => {
    if (!onActivate) return { onKeyDown: undefined, onKeyUp: undefined };
    return {
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onActivate();
        } else if (e.key === ' ') {
          // Prevent page scroll on Space keydown; activation runs on keyup.
          e.preventDefault();
        }
      },
      onKeyUp: (e: React.KeyboardEvent) => {
        if (e.key === ' ') {
          e.preventDefault();
          onActivate();
        }
      },
    };
  }, [onActivate]);
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'stretch',
    gap: tokens.spacingHorizontalL,
    maxWidth: '100%',
    padding: tokens.spacingHorizontalL,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXL}`,
    borderRadius: tokens.borderRadiusLarge,
    border: `1.5px dashed ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    transitionProperty: 'border-color, box-shadow, background-color',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    width: '260px',
    textAlign: 'center' as const,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    // WCAG 2.4.7 Focus Visible: provide a focus ring (with a :focus fallback).
    ':focus': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '2px',
    },
    ':focus:not(:focus-visible)': {
      outlineStyle: 'none',
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '2px',
    },
    '@media (forced-colors: active)': {
      ':focus': {
        outline: '2px solid CanvasText',
        outlineOffset: '2px',
      },
    },
  },
  cardHover: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    backgroundColor: tokens.colorNeutralBackground1Hover,
    boxShadow: tokens.shadow4,
  },
  cardIcon: {
    fontSize: tokens.fontSizeHero700,
    color: tokens.colorBrandForeground1,
  },
  cardTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center' as const,
  },
  cardDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
  },
  dropActive: {
    borderTopColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  separator: {
    alignSelf: 'center',
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    // When the container wraps at narrow widths / high zoom, force the
    // separator onto its own row so 'or' stays visually between the two cards
    // instead of attaching to the first one. Two 260px cards + gap wraps
    // below ~600px.
    '@media (max-width: 600px)': {
      flexBasis: '100%',
      textAlign: 'center' as const,
    },
  },
});

export const EmptyCanvasState: React.FC<EmptyCanvasStateProps> = ({
  onAddTrigger,
  onBrowseTemplates,
  onOperationDrop,
}) => {
  const styles = useStyles();
  const triggerDescId = useId();
  const templateDescId = useId();
  const [triggerHover, setTriggerHover] = useState(false);
  const [templateHover, setTemplateHover] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const triggerKeys = useButtonKeys(onAddTrigger);
  const templateKeys = useButtonKeys(onBrowseTemplates);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!onOperationDrop || !isOperationDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    },
    [onOperationDrop],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    if (related && e.currentTarget.contains(related)) return;
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (!onOperationDrop || !isOperationDrag(e)) return;
      const operation = getOperationFromDrop(e);
      if (operation) onOperationDrop(operation);
    },
    [onOperationDrop],
  );

  return (
    <div
      className={styles.root}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div
        className={styles.container}
        role="group"
        aria-label="Choose how to start your workflow"
      >
        {/* Add trigger node */}
        {onAddTrigger && (
          <div
            className={mergeClasses(styles.card, triggerHover && styles.cardHover)}
            role="button"
            tabIndex={0}
            aria-label="Start from scratch"
            aria-describedby={triggerDescId}
            onClick={onAddTrigger}
            onKeyDown={triggerKeys.onKeyDown}
            onKeyUp={triggerKeys.onKeyUp}
            onMouseEnter={() => setTriggerHover(true)}
            onMouseLeave={() => setTriggerHover(false)}
          >
            <AddCircleRegular className={styles.cardIcon} />
            <Text className={styles.cardTitle}>Start from scratch</Text>
            <Text id={triggerDescId} className={styles.cardDescription}>Pick a trigger and build your workflow step by step</Text>
          </div>
        )}

        {onAddTrigger && onBrowseTemplates && (
          <Text className={styles.separator}>or</Text>
        )}

        {/* Start from a template card */}
        {onBrowseTemplates && (
          <div
            className={mergeClasses(styles.card, templateHover && styles.cardHover)}
            role="button"
            tabIndex={0}
            aria-label="Start from a template"
            aria-describedby={templateDescId}
            onClick={onBrowseTemplates}
            onKeyDown={templateKeys.onKeyDown}
            onKeyUp={templateKeys.onKeyUp}
            onMouseEnter={() => setTemplateHover(true)}
            onMouseLeave={() => setTemplateHover(false)}
          >
            <DocumentBulletListRegular className={styles.cardIcon} />
            <Text className={styles.cardTitle}>Start from a template</Text>
            <Text id={templateDescId} className={styles.cardDescription}>Choose a pre-built workflow for common scenarios</Text>
          </div>
        )}

        {/* Drop zone indicator when dragging */}
        {isDragOver && (
          <div className={mergeClasses(styles.card, styles.dropActive)}>
            <ArrowDownloadRegular className={styles.cardIcon} />
            <Text className={styles.cardTitle}>Drop here</Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmptyCanvasState;
